import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaMicrochip, FaMemory, FaSatelliteDish, FaCodeBranch, FaServer, FaLaptopCode,
    FaArrowRight, FaCheckCircle, FaRocket, FaBug
} from 'react-icons/fa';
import heroImage from '../../assets/Blue and white futuristic structure background illustration ai generativexa _ Premium AI-generated image.png';

const HighTechPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaMicrochip, title: 'Semiconductor Engineering', desc: 'VLSI design, verification, and physical design services.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80' },
        { icon: FaCodeBranch, title: 'Product Engineering', desc: 'Full-lifecycle software product development (ISV).', image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80' },
        { icon: FaMemory, title: 'Embedded Systems', desc: 'Firmware development for RTOS and bare-metal systems.', image: 'https://images.unsplash.com/photo-1597733336794-12d05021d510?w=800&q=80' },
        { icon: FaServer, title: 'Cloud Infrastructure', desc: 'DevOps automation and cloud-native architecture.', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80' },
        { icon: FaSatelliteDish, title: 'IoT Platforms', desc: 'Connectivity solutions for smart devices and sensors.', image: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=800&q=80' },
        { icon: FaLaptopCode, title: 'AI/ML Integration', desc: 'Edge AI models optimized for low-power hardware.', image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80' }
    ];

    const faqs = [
        { q: 'Do you offer FPGA design services?', a: 'Yes, we specialize in Xilinx and Intel FPGA design for high-performance computing and acceleration.' },
        { q: 'What is your verification methodology?', a: 'We use UVM (Universal Verification Methodology) and SystemVerilog for robust IP verification.' },
        { q: 'Can you help with legacy code refactoring?', a: 'Absolutely. We modernize legacy C/C++ codebases to modern standards (C++17/20) and microservices.' },
        { q: 'Do you support RISC-V architecture?', a: 'Yes, we have extensive experience in designing and verifying RISC-V based cores and SoCs.' },
        { q: 'How do you handle IP protection?', a: 'We follow strict clean-room protocols and use encrypted EDA environments to ensure zero IP leakage.' },
        { q: 'Can you assist with taped-out chips?', a: 'We provide post-silicon validation and driver development services to bring your chips to market faster.' }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section - Tech/Cyan Theme */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    {/* Clean overlay, no white shadow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-900/70 to-transparent mix-blend-multiply"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-cyan-300 mb-4">Industries &gt; Manufacturing</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">High <span className="font-bold text-cyan-400">Tech</span></h1>
                        <p className="text-lg font-light text-cyan-100 leading-relaxed mb-8">
                            Accelerating innovation from silicon to software.
                        </p>
                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-3 bg-cyan-600 text-white font-medium rounded-sm hover:bg-cyan-700 transition-all text-sm uppercase tracking-wide">
                                Engineering Services
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Introduction - HCL Style Text Left, Image Right */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-light mb-6 text-slate-900">Engineering the Future</h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                The pace of innovation in the high-tech sector is relentless. Tech-Inovant acts as an engineering multiplier for semiconductor firms, ISVs, and platform companies.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                We bridge the gap between hardware constraints and software possibilities, delivering robust, scalable, and market-ready products.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-cyan-600" />
                                    <span className="text-slate-700 font-medium">Faster Time-to-Market</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-cyan-600" />
                                    <span className="text-slate-700 font-medium">Silicon-Level Optimization</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-cyan-600" />
                                    <span className="text-slate-700 font-medium">Scalable Cloud Backends</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            {/* Clean Image */}
                            <img
                                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80"
                                alt="High Tech Innovation"
                                className="rounded-sm shadow-sm"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-cyan-600 text-white p-6 rounded-sm shadow-lg hidden md:block">
                                <div className="text-3xl font-bold">500+</div>
                                <div className="text-xs uppercase tracking-wider">Engineers</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid - Clean Cards */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="mb-16">
                        <h2 className="text-3xl font-light mb-4 text-slate-900">R&D Capabilities</h2>
                        <div className="w-20 h-1 bg-cyan-600"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white border border-slate-200 hover:border-cyan-400 transition-colors p-0 overflow-hidden group">
                                <div className="h-48 overflow-hidden relative">
                                    {/* Clear Image - No White Shadow */}
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-8">
                                    <service.icon className="text-3xl text-cyan-600 mb-4" />
                                    <h3 className="text-xl font-medium mb-3 text-slate-900">{service.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                        {service.desc}
                                    </p>
                                    <a href="#" className="inline-flex items-center text-sm font-bold text-cyan-700 hover:text-cyan-900 tracking-wide uppercase">
                                        View Details <FaArrowRight className="ml-2 text-xs" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Metrics - Minimalist */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <h2 className="text-3xl font-light mb-8 text-center text-slate-900">Proven Success</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Top 5 Semi Co.', stat: '30%', desc: 'Reduction in verification cycle time.' },
                            { title: 'IoT Platform', stat: '10M+', desc: 'Devices connected globally.' },
                            { title: 'Storage Leader', stat: 'Zero', desc: 'Critical bugs in firmware release.' }
                        ].map((caseStudy, idx) => (
                            <div key={idx} className="bg-slate-50 p-8 border-l-4 border-cyan-500">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{caseStudy.title}</div>
                                <div className="text-4xl font-light text-cyan-800 mb-4">{caseStudy.stat}</div>
                                <p className="text-slate-600">{caseStudy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Section */}
            <section id="contact" className="py-12 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-light mb-8 text-slate-900">Tech Services FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-white border border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-50"
                                        >
                                            <span className="font-medium text-slate-800">{faq.q}</span>
                                            <span className="text-slate-400">{openFaq === idx ? '-' : '+'}</span>
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                                                {faq.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-200">
                            <h3 className="text-2xl font-light mb-6 text-slate-900">Scale Your Team</h3>
                            <p className="text-slate-600 mb-8">Find the right engineering talent today.</p>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-cyan-500 outline-none transition-colors" placeholder="Alex Tech" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-cyan-500 outline-none transition-colors" placeholder="TechWorks" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-cyan-500 outline-none transition-colors" placeholder="alex@techworks.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Requirement</label>
                                    <select className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-cyan-500 outline-none transition-colors text-slate-700">
                                        <option>Chip Design</option>
                                        <option>Embedded Software</option>
                                        <option>Cloud Architecture</option>
                                        <option>Product Engineering</option>
                                    </select>
                                </div>
                                <button type="submit" className="w-full bg-cyan-600 text-white font-bold py-4 rounded-sm hover:bg-cyan-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Contact Sales
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-12 bg-slate-900 text-white text-center">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-light mb-6">Partner with Tech Experts</h2>
                    <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                        Scale your engineering team with our domain specialists.
                    </p>
                    <button className="px-8 py-3 bg-white text-slate-900 font-bold uppercase tracking-wide hover:bg-cyan-50 transition-colors">
                        Talk to an Engineer
                    </button>
                </div>
            </section>
        </div>
    );
};

export default HighTechPage;
